import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Eye, Play, SkipForward, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";
import { useRoomSocket } from "@/lib/ws";
import { ControlBar } from "@/components/game/ControlBar";
import { studentTokenKey } from "@/pages/JoinPage";
import { previewPlaylist } from "@/pages/teacher/PlaylistsPage";

export function PreviewStudent() {
  const { code } = useParams();
  const [ready, setReady] = useState(Boolean(localStorage.getItem(studentTokenKey(code))));
  useEffect(() => {
    if (ready) return;
    api.post("/play/join", { code, first_name: "Önizleme", last_name: "Öğrenci" })
      .then((r) => { localStorage.setItem(studentTokenKey(code), r.data.token); setReady(true); })
      .catch(() => setReady(true));
  }, [code, ready]);
  return ready ? <Navigate to={`/oyun/${code}`} replace /> : <p className="p-6 eyebrow">Öğrenci katılıyor…</p>;
}

export default function PreviewPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const { state, send } = useRoomSocket(`/teacher/${code}?token=${encodeURIComponent(token || "")}`);
  const [params] = useSearchParams();
  const pl = params.get("pl");
  const idx = Number(params.get("i") || 0);
  const total = Number(params.get("n") || 0);
  const next = async () => {
    localStorage.removeItem(studentTokenKey(code));
    const url = await previewPlaylist({ id: pl, items: { length: total } }, idx + 1);
    if (url) navigate(url);
  };
  const launch = async () => {
    try {
      const { data } = await api.post(`/preview/${code}/launch`);
      localStorage.removeItem(studentTokenKey(code));
      toast.success(`Gerçek oyun açıldı: ${data.code}`);
      navigate(`/ogretmen/oyun/${data.code}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const close = async () => {
    await api.delete(`/preview/${code}`).catch(() => {});
    localStorage.removeItem(studentTokenKey(code));
    if (window.opener) window.close(); else navigate("/ogretmen/oyunlar");
  };
  return (
    <div className="min-h-screen flex flex-col gap-3 p-3" data-testid="preview-page">
      <div className="panel p-3 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2 font-display text-xl font-bold"><Eye className="h-5 w-5 text-[#FACC15]" />Önizleme</span>
        <span className="text-sm text-slate-400">Kayıt, analiz ve rekor tutulmaz. Tahtada ve sanal telefonda oynayarak deneyin.</span>
        {pl && <span className="text-sm font-semibold text-[#FACC15]" data-testid="preview-playlist-position">Liste: {idx + 1} / {total}</span>}
        {pl && idx + 1 < total && <Button size="sm" onClick={next} data-testid="preview-playlist-next"><SkipForward className="h-4 w-4 mr-1" />Sıradaki oyunu önizle</Button>}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" onClick={launch} className="font-bold" data-testid="preview-launch"><Play className="h-4 w-4 mr-1" />Bu oyunu başlat</Button>
          <Button variant="outline" size="sm" onClick={close} data-testid="preview-close"><X className="h-4 w-4 mr-1" />Önizlemeyi kapat</Button>
        </div>
      </div>
      {state && <div className="panel p-3" data-testid="preview-controls"><ControlBar state={state} send={send} /></div>}
      <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0">
        <div className="flex-1 min-h-[50vh] rounded-2xl overflow-hidden border border-white/10">
          <iframe key={code} title="Tahta" src={`/tahta/${code}`} className="w-full h-full min-h-[50vh] bg-black" data-testid="preview-board-frame" />
        </div>
        <div className="mx-auto shrink-0 w-[380px] max-w-full h-[760px] rounded-[2.5rem] border-[10px] border-slate-800 overflow-hidden shadow-2xl bg-black">
          <iframe key={`p-${code}`} title="Öğrenci telefonu" src={`/onizleme-ogrenci/${code}`} className="w-full h-full" data-testid="preview-phone-frame" />
        </div>
      </div>
    </div>
  );
}
